import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Database, PenTool, Phone, Calendar } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function AboutPage() {
  return (
    <MarketingLayout>
      <SEO
        title="About | Prozpector"
        description="Meet Scott Dudley - data analyst turned copywriter turned SDR. Learn how Prozpector was built to solve the outbound problem from the inside out."
        url="https://prozpector.com/about"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-32">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Built by Someone Who's
              <br />
              Been in the Trenches
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              I built Prozpector because I was tired of watching good products fail due to bad outbound. This is the system I wish I had years ago.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">My Story</h2>
            <div className="space-y-6 text-lg text-gray-600">
              <p>
                I started in data. Spent years building dashboards, analyzing metrics, and figuring out what actually moves the needle for businesses. That taught me one thing: <strong>most companies don't have a product problem—they have a pipeline problem.</strong>
              </p>
              <p>
                Then I got into copywriting. Learned what makes people respond, what gets ignored, and why most cold emails end up in the trash. Turns out, personalization at scale isn't just about inserting {"{{first_name}}"}—it's about understanding the person on the other end.
              </p>
              <p>
                Finally, I did the SDR grind myself. The manual prospecting. The endless LinkedIn scrolling. The copy-paste follow-ups. The spreadsheet chaos. I felt the pain firsthand.
              </p>
              <p>
                <strong>So I built Prozpector to solve my own problem.</strong>
              </p>
              <p>
                Not a generic tool that promises magic. A system that combines data-driven targeting, copywriting that actually converts, and the persistence that gets meetings booked. The same system I use for myself, now available as a done-for-you service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three Skills Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Three Skills. One System.</h2>
            <p className="text-xl text-gray-600">
              Most outbound fails because it's missing at least one of these
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8">
              <div className="h-16 w-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <Database className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Data</h3>
              <p className="text-gray-600 text-center">
                Years of analytics work taught me how to find the right prospects, not just any prospects. Quality lists are the foundation.
              </p>
            </Card>

            <Card className="p-8">
              <div className="h-16 w-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <PenTool className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Copywriting</h3>
              <p className="text-gray-600 text-center">
                Messages that sound human, speak to real pain points, and give people a reason to respond. No templates, no spam.
              </p>
            </Card>

            <Card className="p-8">
              <div className="h-16 w-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">SDR Persistence</h3>
              <p className="text-gray-600 text-center">
                Most deals happen after the 5th touchpoint. I built systems to follow up consistently without being annoying.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Why I Built It Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Why a Done-For-You Service?</h2>
            <div className="space-y-6 text-lg text-gray-600">
              <p>
                I've seen too many founders and sales leaders buy expensive tools, spend weeks setting them up, and still end up with an empty calendar. The tool isn't the problem—<strong>the execution is.</strong>
              </p>
              <p>
                Building prospect lists takes skill. Writing copy that converts takes practice. Running consistent outreach takes discipline. Most teams don't have time for all three.
              </p>
              <p>
                That's why Prozpector isn't just a platform—it's a service. I handle the entire outbound process so you can focus on what you're good at: having conversations and closing deals.
              </p>
              <p>
                No learning curve. No setup headaches. Just qualified meetings on your calendar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How We Work Together</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex gap-6 items-start">
              <div className="h-10 w-10 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Discovery Call</h3>
                <p className="text-gray-600">
                  We talk about your ideal customer, what's worked before, what hasn't, and what success looks like.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="h-10 w-10 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">I Build Your System</h3>
                <p className="text-gray-600">
                  Prospect lists, email copy, outreach sequences—all customized to your business. You approve before anything goes out.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="h-10 w-10 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Campaigns Go Live</h3>
                <p className="text-gray-600">
                  I run your outbound daily. You get weekly reports and meetings booked directly on your calendar.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="h-10 w-10 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Continuous Optimization</h3>
                <p className="text-gray-600">
                  I analyze what's working, refine the messaging, and improve targeting. Your results get better over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Fill Your Calendar?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's talk about your outbound and see if we're a good fit.
            </p>
            <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="bg-white text-black hover:bg-gray-100 border-white text-lg px-8 py-6">
                <Calendar className="mr-2 h-5 w-5" />
                Book a Discovery Call
              </Button>
            </a>
            <p className="text-sm text-gray-400 mt-6">
              15 minutes. No pressure. Just a conversation about your pipeline.
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
